
import axios from "axios";
import { useContext, useLayoutEffect, useCallback } from 'react';

import * as Constants from '../constants';
import { AuthStateContext } from '../contexts/auth-state-context';
import { GlobalContext } from "../contexts/global-context";

export const authenticatedAxios = axios.create({
    baseURL: Constants.API_HOST,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    }
});

const unauthenticatedAxios = axios.create({
    baseURL: Constants.API_HOST,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    }
});

function configureAuthenticatedAxiosInterceptors({
  accessTokenRef,
  handleAuthRequired
}) {
  // Clear existing interceptors if needed (optional, only if you need to ensure no duplicates):
  authenticatedAxios.interceptors.request.handlers = [];
  authenticatedAxios.interceptors.response.handlers = [];

  // Set up the request interceptor
  authenticatedAxios.interceptors.request.use(
    (config) => {
      // Move login message or custom data handling here if needed
      if (config._loginMessage) {
        config.customData = config.customData || {};
        config.customData._loginMessage = config._loginMessage;
      }

      if (accessTokenRef.current) {
        config.headers["Authorization"] = `Bearer ${accessTokenRef.current}`;
        return config;
      } else {
        if (config.skipReAuth) {
          return config;
        } else {
          return handleAuthRequired(config);
        }
      }
    },
    (error) => Promise.reject(error)
  );

  // Set up the response interceptor if you need it
  authenticatedAxios.interceptors.response.use(
    (response) => response,
    async (error) => {

      if (!error || !error.config) {
        return Promise.reject(error);
      }

      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        if (originalRequest.skipReAuth) {
          return Promise.reject(error);
        } else {
          return handleAuthRequired(originalRequest);
        }
      }

      return Promise.reject(error);
    }
  );
}

function createHandleAuthRequired({
  setShowAuthModal,
  setLoginMessage,
  setAuthRedirectPath,
  pendingRequests
}) {
  return function handleAuthRequired(config) {
    const message = config.customData?._loginMessage || '';
    setLoginMessage(message);
    setShowAuthModal(true);

    setAuthRedirectPath(window.location.pathname);

    return new Promise((resolve, reject) => {
      pendingRequests.current.push({ resolve, reject, config });
    });
  };
}

export function ApiProvider({ children }) {
  const { accessTokenRef, pendingRequests, setAuthRedirectPath } = useContext(AuthStateContext);
  const { setShowAuthModal, setLoginMessage } = useContext(GlobalContext);

  useLayoutEffect(() => {
    const handleAuthRequired = createHandleAuthRequired({
      setShowAuthModal,
      setLoginMessage,
      setAuthRedirectPath,
      pendingRequests
    });

    configureAuthenticatedAxiosInterceptors({
      accessTokenRef,
      pendingRequests,
      handleAuthRequired
    });

  }, [accessTokenRef, pendingRequests, setShowAuthModal, setLoginMessage, setAuthRedirectPath]);

  return <>{children}</>;
}

function useApi() {
    const { clearUser, suppressAuthRedirectRef } = useContext(AuthStateContext);

    const handleApiError = useCallback(
      (error) => {
        if (error.response) {
          const status = error.response.status;
    
          // Suppress expected validation errors (e.g., form validation)
          if (status === 400) return;
    
          // You can add other suppressions if needed
        } else if (error.request) {
        } else {
        }
      },
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Authenticated request functions
    const apiGetAuthenticated = useCallback(
      async (url, config = {}) => {
        try {
          const response = await authenticatedAxios.get(url, config);
          return response.data;
        } catch (error) {
          handleApiError(error);
          throw error;
        }
      }, [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const apiPostAuthenticated = useCallback(
      async (url, data, config = {}) => {
        try {
          return await authenticatedAxios.post(url, data, config);
        } catch (error) {
          handleApiError(error);
          throw error;
        }
      }, [] // eslint-disable-line react-hooks/exhaustive-deps
    );
  
    const apiDeleteAuthenticated = useCallback(
      async (url, config = {}) => {
        try {
          const response = await authenticatedAxios.delete(url, config);
          return response.data;
        } catch (error) {
          handleApiError(error);
          throw error;
        }
      },
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // const apiPatchAuthenticated = async (url, data, config = {}) => {
    //   try {
    //     return await authenticatedAxios.patch(url, data, config);
    //   } catch (error) {
    //     handleApiError(error);
    //     throw error;
    //   }
    // };

    // Unauthenticated request functions
    const apiGetUnauthenticated = useCallback(
      async (url) => {
        try {
          const response = await unauthenticatedAxios.get(url);
          return response.data;
        } catch (error) {
          handleApiError(error);
          throw error;
        }
      }, [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const apiPostUnauthenticated = useCallback(async (url, data) => {
      try {
          return await unauthenticatedAxios.post(url, data);
        } catch (error) {
          handleApiError(error);
          throw error;
        }
      },[] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const initUserModel = useCallback((data) =>
      apiPostAuthenticated(
        Constants.INIT_USERMODEL_URL,
        data,
        { _loginMessage: 'Creating custom models is completely free! You just need to log in or create an account first.' }
      ), [] // eslint-disable-line react-hooks/exhaustive-deps
    );
  
    const generateUserModel = useCallback((data) =>
      apiPostAuthenticated(
        Constants.GENERATE_USERMODEL_URL,
        data,
        { _loginMessage: 'Creating custom models is completely free! You just need to log in or create an account first.' }
      ),[] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const uploadBuildPhoto = useCallback((model, data) =>
      apiPostAuthenticated(
        `${Constants.MODEL_URL}${model}${Constants.UPLOAD_PHOTO_URL}`,
        data,
        {
          _loginMessage: 'To upload build photos, you just need to log in or create an account first.',
          headers: { 'Content-Type': 'multipart/form-data' }
         }
      ),[] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const uploadProfilePhoto = useCallback((data) =>
      apiPostAuthenticated(
        Constants.USER_PROFILE_UPLOAD_URL,
        data,
        {
          _loginMessage: 'To upload a profile photo, you just need to log in or create an account first.',
          headers: { 'Content-Type': 'multipart/form-data' }
         }
      ),[] // eslint-disable-line react-hooks/exhaustive-deps
    );
  
    const deleteProfilePhoto = useCallback(
      () =>
        apiDeleteAuthenticated(
          Constants.USER_PROFILE_UPLOAD_URL,
        ),
      [] // eslint-disable-line
    );

    const deleteUserAccount = useCallback(
      () =>
        apiDeleteAuthenticated(
          Constants.USER_PROFILE_DELETE,
        ),
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const addUserEmail = useCallback(
      (emailData, sessionToken = null) => {
        const config = {};
        if (sessionToken) {
          config.headers = {
            'X-Session-Token': sessionToken
          };
        }
        return unauthenticatedAxios.post(
          Constants.USER_ADD_EMAIL_URL,
          emailData,
          config
        );
      },
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const createPlan = useCallback((data) =>
      apiPostAuthenticated(
        Constants.PLAN_URL,
        data,
        { _loginMessage: 'To create a plan, please log in or create an account first.' }
      ), [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const createCheckoutSession = useCallback((data) =>
      apiPostAuthenticated(
        `${Constants.API_BASE}/plan/checkout/`,
        data,
        { _loginMessage: 'To create a plan, please log in or create an account first.' }
      ), [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const validateCoupon = useCallback((couponCode, modelName) =>
      apiPostAuthenticated(
        `${Constants.API_BASE}/coupons/validate/`,
        { 
          coupon_code: couponCode,
          model: modelName 
        }
      ), [] // eslint-disable-line react-hooks/exhaustive-deps
    );
  
    const getPlans = useCallback(
      (page = 1, page_size) => {
        let url = `${Constants.PLANS_URL}?page=${page}`;
        if (page_size != null) {
          url += `&page_size=${page_size}`;
        }
        return apiGetAuthenticated(
          url,
          { _loginMessage: 'To access your plans, please log in or create an account first.' }
        );
      }, [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const getPlan = useCallback((planId) =>
        apiGetAuthenticated(
          `${Constants.PLAN_URL}${planId}/`,
          { _loginMessage: 'To access this plan, please log in or create an account first.' }
        ), [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const getUserModels = useCallback(
      (page = 1, page_size) => {
        let url = `${Constants.USERMODELS_URL}?page=${page}`;
        if (page_size != null) {
          url += `&page_size=${page_size}`;
        }
        return apiGetAuthenticated(
          url,
          { _loginMessage: 'To access your custom models, please log in or create an account first.' }
        );
      }, [] // eslint-disable-line react-hooks/exhaustive-deps
    );
  
    const getUser = useCallback(() =>
        apiGetAuthenticated(
          `${Constants.USER_URL}`,
          { _loginMessage: 'To access your settings, please log in.' }
        ), [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const logoutUser = useCallback(() => {
      suppressAuthRedirectRef.current = true;
      
      // Just clear client-side tokens and redirect
      clearUser();
      window.location.replace("/");
    }, [clearUser]); // eslint-disable-line react-hooks/exhaustive-deps
  
    // Unauthenticated API calls
    const getModel = useCallback(
      (modelId) => apiGetUnauthenticated(`${Constants.MODEL_URL}${modelId}/`),
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const getModels = useCallback(
      (page = 1, page_size, ordering = 'popular', search = '', tags = []) => {
        let url = `${Constants.MODELS_URL}?page=${page}`;
        if (page_size != null) {
          url += `&page_size=${page_size}`;
        }
        if (ordering) {
          url += `&ordering=${ordering}`;
        }
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        if (tags && tags.length > 0) {
          tags.forEach(tag => {
            url += `&tags=${encodeURIComponent(tag)}`;
          });
        }
        return apiGetUnauthenticated(url);
      }, [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const getTags = useCallback(
      () => apiGetUnauthenticated(Constants.TAGS_URL),
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );
  
    const getModelPhotos = useCallback(
      (modelId, page = 1) => 
        apiGetUnauthenticated(`${Constants.MODEL_URL}${modelId}/photos/?page=${page}`),
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );
  
    const getStaticResource = useCallback(
      (path) => apiGetUnauthenticated(`${Constants.STATIC_URL}${path}`),
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );
  
    const registerUser = useCallback(
      (regData) => apiPostUnauthenticated(Constants.REGISTER_URL, regData),
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );
  
    const resendConfirmEmail = useCallback(
      (sessionToken) => {
        const config = {};
        if (sessionToken) {
          config.headers = { 'X-Session-Token': sessionToken };
        }
        return unauthenticatedAxios.post(Constants.RESEND_EMAIL_URL, {}, config);
      },
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const confirmEmail = useCallback(
      (code, sessionToken) => {
        const config = {};
        if (sessionToken) {
          config.headers = { 'X-Session-Token': sessionToken };
        }
        return unauthenticatedAxios.post(Constants.EMAIL_CONFIRM_URL, { key: code }, config);
      },
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );
  
    const requestPasswordReset = useCallback(
      (regData) => apiPostUnauthenticated(Constants.REQ_PASSWORD_RESET, regData),
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );
  
    const passwordReset = useCallback(
      (regData) => apiPostUnauthenticated(Constants.PASSWORD_RESET, regData),
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );
  
    const loginUser = useCallback(
      (loginData) => apiPostUnauthenticated(Constants.LOGIN_URL, loginData),
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );


    const loginWithProvider = async (provider, tokenOrCode, isAccessToken = false) => {
      try {
        const payload = {
          provider: provider,
          process: "login"
        };

        if (isAccessToken) {
          // New allauth format with tokens
          const clientId = provider === 'google' 
            ? process.env.REACT_APP_GOOGLE_CLIENT_ID 
            : process.env.REACT_APP_FACEBOOK_APP_ID;
          
          payload.token = {
            client_id: clientId
          };
          
          if (provider === 'google') {
            // Google sends ID token
            payload.token.id_token = tokenOrCode;
          } else if (provider === 'facebook') {
            // Facebook sends access token
            payload.token.access_token = tokenOrCode;
          }

        } else {
          // Authorization code format for new allauth
          payload.code = tokenOrCode;
          payload.redirect_uri = `${window.location.origin}/auth/callback`;
        }

        const response = await unauthenticatedAxios.post(`${Constants.LOGIN_PROVIDER_URL}${provider}/`, payload);
        return response.data;
      } catch (error) {
        handleApiError(error);
        throw error;
      }
    };

    const sendContactEmail = useCallback((contactData) => {
        return apiPostUnauthenticated(Constants.CONTACT, contactData);
    }, [apiPostUnauthenticated]);

    const getAppConfig = useCallback(() => {
        return apiGetUnauthenticated(Constants.CONFIG);
    }, [apiGetUnauthenticated]);

    const updateTutorialFlags = useCallback(async (flags) => {
        try {
            return await authenticatedAxios.patch(`${Constants.API_HOST}/api/v1/user/tutorial-flags/`, {
                flags: flags
            });
        } catch (error) {
            handleApiError(error);
            throw error;
        }
    }, [handleApiError]);

    const updateUserModelName = useCallback(async (userModelId, name) => {
        try {
            return await authenticatedAxios.patch(`${Constants.API_HOST}/api/v1/usermodel/${userModelId}/update/`, {
                name: name
            });
        } catch (error) {
            handleApiError(error);
            throw error;
        }
    }, [handleApiError]);

    const deleteUserModel = useCallback(async (userModelId) => {
        try {
            const response = await authenticatedAxios.delete(`${Constants.API_HOST}/api/v1/usermodel/${userModelId}/delete/`);
            return response;
        } catch (error) {
            handleApiError(error);
            throw error;
        }
    }, [handleApiError]);
  
    return {
      initUserModel,
      generateUserModel,
      getPlans,
      getPlan,
      getUserModels,
      getUser,
      createPlan,
      createCheckoutSession,
      validateCoupon,
      logoutUser,
      getModel,
      getModels,
      getTags,
      getModelPhotos,
      getStaticResource,
      registerUser,
      resendConfirmEmail,
      confirmEmail,
      requestPasswordReset,
      passwordReset,
      loginUser,
      loginWithProvider,
      uploadBuildPhoto,
      uploadProfilePhoto,
      deleteProfilePhoto,
      deleteUserAccount,
      addUserEmail,
      sendContactEmail,
      getAppConfig,
      updateTutorialFlags,
      updateUserModelName,
      deleteUserModel
    };
};

export default useApi;